import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useUserDetailsStore } from '@zeta-core';
import { FinancialPlanningService, InvestorService } from '@zeta-services';
import { IClientCardItem, IGetClientAndPartnerDetailsRequest, IGetClientAndPartnerDetailsResponse, ISourceDetailRequest } from '@zeta-types';
import { useLocation } from 'react-router';

interface IFactFindLayoutType {
  children: ReactNode;
  isClient?: boolean;
}

interface SourceControlContextType {
  getSourceDetail: (payload: ISourceDetailRequest) => Promise<any>;
  isPending: boolean;
  data: any;
  clientPartnerDetails: any;
}

const SourceControlContext = createContext<SourceControlContextType | null>(null);

export const useSourceControlContext = () => useContext(SourceControlContext) || {};

export const FactFindLayoutContextComponent = ({ isClient, children }: IFactFindLayoutType) => {
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  const location = useLocation();
  let state = (location.state || {}) as IClientCardItem;
  if (isClient) {
    state = {
      clientData: {
        clientID: accessibleEntityID,
      },
    };
  }
  const { isPending, data, mutate, mutateAsync } = useMutation({
    mutationFn: (payload: ISourceDetailRequest) => InvestorService().getSourceDetail(payload),
  });
  const [clientPartnerDetails, setClientPartnerDetails] = useState<IGetClientAndPartnerDetailsResponse>({});
  const getClientPartnerDetails = useMutation({
    mutationFn: (payload: IGetClientAndPartnerDetailsRequest) => FinancialPlanningService().getClientandPartnerDetails(payload),

    onSuccess: (response) => {
      setClientPartnerDetails(response?.data);
    },
  });

  useEffect(() => {
    if (state?.clientData?.clientID) {
      mutate({ clientID: state?.clientData?.clientID });
      getClientPartnerDetails.mutate({ clientId: state?.clientData?.clientID });
    }
  }, [state?.clientData?.clientID]);

  const contextValue = useMemo(
    () => ({
      data: data?.data?.response,
      isPending,
      mutateAsync,
      clientPartnerDetails: clientPartnerDetails,
    }),
    [data?.data?.response, isPending, clientPartnerDetails]
  );

  return <SourceControlContext.Provider value={contextValue}>{children}</SourceControlContext.Provider>;
};
